import { makeAutoObservable, runInAction } from 'mobx';
import { queryUserApps } from '@/api';
import {
  getAutocompleteSuggestions,
  validateCommand,
  generatePrompt,
  extractNamespaceFromCommand,
  removeNamespaceFromCommand
} from '@/utils/kubectlHelper';
import kubectlWebSocketService from '@/services/kubectlWebSocketService';

class KubectlConsole {
  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });

    // 从 localStorage 读取主题设置
    const savedTheme = localStorage.getItem('kubectl-console-theme');
    this.theme = savedTheme || 'dark';

    // 从 localStorage 读取字号设置
    const savedFontSize = localStorage.getItem('kubectl-console-fontsize');
    this.fontSize = savedFontSize || '1x';
  }

  // 状态
  clusters = [];
  namespaces = {}; // { clusterId: [namespaces] }
  tabs = [];
  activeTabId = null;
  wsConnected = false; // WebSocket 连接状态

  // 主题设置
  theme = 'dark'; // 'dark' | 'light'

  // 字号设置
  fontSize = '1x'; // '1x' | '1.5x' | '2x' | '3x'

  // 加载状态
  clustersLoading = false;
  namespacesLoading = false;

  // 错误信息
  error = null;

  // 初始化
  async init(initialCluster = null, initialNamespace = null) {
    // 初始化 WebSocket 连接
    this.initWebSocket();

    await this.fetchClusters();

    // 确定要使用的集群
    let targetCluster = initialCluster;
    if (!targetCluster && this.clusters.length > 0) {
      targetCluster = this.clusters[0].id;
    }

    // 创建默认标签页
    if (targetCluster) {
      await this.addTab(targetCluster, initialNamespace);
    }
  }

  // 初始化 WebSocket 连接
  initWebSocket() {
    // 设置 token 获取函数
    kubectlWebSocketService.setTokenGetter(() => {
      return localStorage.getItem('token');
    });

    kubectlWebSocketService.onConnectionStatusChange((connected) => {
      runInAction(() => {
        this.wsConnected = connected;
        // 不再显示连接状态变化消息，避免干扰用户
        // 如需调试，可以取消下面的注释
        // this.tabs.forEach(tab => {
        //   const statusMessage = connected
        //     ? { type: 'success', content: 'WebSocket connected', timestamp: new Date().toISOString() }
        //     : { type: 'warning', content: 'WebSocket disconnected, reconnecting...', timestamp: new Date().toISOString() };
        //   tab.outputs.push(statusMessage);
        // });
      });
    });

    kubectlWebSocketService.setErrorHandler((error) => {
      runInAction(() => {
        this.error = error.message;
      });
    });

    kubectlWebSocketService.connect();
  }

  // 获取集群列表
  async fetchClusters() {
    this.clustersLoading = true;
    this.error = null;

    try {
      const res = await queryUserApps();

      if (res?.code === 200 && res?.data) {
        // 从应用列表中提取唯一的集群信息
        const clusterMap = new Map();

        res.data.forEach(app => {
          const clusterId = app.cluster;
          if (!clusterMap.has(clusterId)) {
            clusterMap.set(clusterId, {
              id: clusterId,
              name: clusterId,
              region: app.envName?.toLowerCase() || 'unknown',
              status: app.status?.toLowerCase() || 'active',
              provider: app.cloudProvider || 'AWS',
              endpoint: null // 从 API 返回中没有 endpoint 信息
            });
          }
        });

        runInAction(() => {
          this.clusters = Array.from(clusterMap.values());
          this.clustersLoading = false;
        });
      } else {
        throw new Error(res?.msg || 'Failed to fetch clusters');
      }
    } catch (err) {
      runInAction(() => {
        this.error = err.message;
        this.clustersLoading = false;
      });
    }
  }

  // 获取命名空间列表
  async fetchNamespaces(clusterId) {
    this.namespacesLoading = true;
    this.error = null;

    try {
      const res = await queryUserApps();

      if (res?.code === 200 && res?.data) {
        // 从应用列表中提取指定集群的命名空间
        const namespaceSet = new Set();

        res.data.forEach(app => {
          if (app.cluster === clusterId && app.nameSpace) {
            namespaceSet.add(app.nameSpace);
          }
        });

        const namespaces = Array.from(namespaceSet).sort();

        runInAction(() => {
          this.namespaces[clusterId] = namespaces;
          this.namespacesLoading = false;
        });

        return namespaces;
      } else {
        throw new Error(res?.msg || 'Failed to fetch namespaces');
      }
    } catch (err) {
      runInAction(() => {
        this.error = err.message;
        this.namespacesLoading = false;
      });
      return [];
    }
  }

  // 最大标签页数量
  MAX_TABS = 10;

  // 添加新标签页
  async addTab(clusterId, namespace = null) {
    // 检查标签页数量限制
    if (this.tabs.length >= this.MAX_TABS) {
      return { error: `Maximum ${this.MAX_TABS} tabs allowed` };
    }

    const tabId = `tab-${Date.now()}`;
    const cluster = this.clusters.find(c => c.id === clusterId);

    // 确保有命名空间数据，等待加载完成
    if (!this.namespaces[clusterId]) {
      await this.fetchNamespaces(clusterId);
    }

    // 如果没有指定命名空间，使用第一个可用的
    let effectiveNamespace = namespace;
    if (!effectiveNamespace) {
      const availableNamespaces = this.namespaces[clusterId] || [];
      effectiveNamespace = availableNamespaces.length > 0 ? availableNamespaces[0] : null;
    }

    // 检查是否有可用的命名空间
    const hasNamespaces = this.namespaces[clusterId]?.length > 0;

    // 生成标签页标题
    const tabTitle = effectiveNamespace
      ? `${cluster?.name || clusterId} - ${effectiveNamespace}`
      : cluster?.name || clusterId;

    const newTab = {
      id: tabId,
      title: tabTitle,
      clusterId,
      namespace: effectiveNamespace,
      status: 'disconnected', // disconnected, connecting, connected
      outputs: [], // 命令输出历史
      currentInput: '', // 当前输入框内容
      commandHistory: [], // 命令历史
      historyIndex: -1, // 历史记录索引
      cursorPosition: 0, // 光标位置
      executing: false, // 是否正在执行命令
      currentSessionId: null, // 当前 WebSocket 会话 ID
      disabled: !hasNamespaces, // 是否禁用输入框（没有命名空间时禁用）
    };

    runInAction(() => {
      this.tabs.push(newTab);
      this.activeTabId = tabId;
    });

    return tabId;
  }

  // 关闭标签页
  closeTab(tabId) {
    runInAction(() => {
      const index = this.tabs.findIndex(tab => tab.id === tabId);
      if (index === -1) return;

      this.tabs.splice(index, 1);

      // 如果关闭的是当前激活的标签，切换到另一个
      if (this.activeTabId === tabId) {
        if (this.tabs.length > 0) {
          this.activeTabId = this.tabs[Math.max(0, index - 1)].id;
        } else {
          this.activeTabId = null;
        }
      }
    });
  }

  // 设置激活标签
  setActiveTab(tabId) {
    runInAction(() => {
      this.activeTabId = tabId;
    });
  }

  // 更新标签页集群
  async updateTabCluster(tabId, clusterId) {
    runInAction(() => {
      const tab = this.tabs.find(t => t.id === tabId);
      if (!tab) return;

      tab.clusterId = clusterId;
      tab.status = 'connecting';
    });

    // 获取命名空间
    await this.fetchNamespaces(clusterId);

    runInAction(() => {
      const tab = this.tabs.find(t => t.id === tabId);
      if (!tab) return;

      // 选择第一个可用的命名空间
      const availableNamespaces = this.namespaces[clusterId] || [];
      const targetNamespace = availableNamespaces.length > 0 ? availableNamespaces[0] : null;

      tab.namespace = targetNamespace;
      tab.status = 'connected';

      // 根据是否有命名空间设置禁用状态
      tab.disabled = availableNamespaces.length === 0;

      // 生成标签页标题
      const cluster = this.clusters.find(c => c.id === clusterId);
      tab.title = targetNamespace
        ? `${cluster?.name || clusterId} - ${targetNamespace}`
        : cluster?.name || clusterId;
    });
  }

  // 更新标签页命名空间
  updateTabNamespace(tabId, namespace) {
    runInAction(() => {
      const tab = this.tabs.find(t => t.id === tabId);
      if (!tab) return;

      tab.namespace = namespace;

      const cluster = this.clusters.find(c => c.id === tab.clusterId);
      tab.title = `${cluster?.name || tab.clusterId} - ${namespace}`;
    });
  }

  // 发送命令
  async sendCommand(tabId, command) {
    const tab = this.tabs.find(t => t.id === tabId);
    if (!tab) return;

    const trimmedCommand = command.trim();

    // 检查是否是 clear 命令（支持 kubectl clear 和 clear 两种格式）
    const commandLower = trimmedCommand.toLowerCase();
    if (commandLower === 'clear' || commandLower === 'kubectl clear') {
      // clear 命令不需要添加到历史记录，直接清屏
      this.clearOutputs(tabId);
      return;
    }

    // 检查命令是否以 kubectl 开头（不区分大小写）
    if (!trimmedCommand.toLowerCase().startsWith('kubectl')) {
      runInAction(() => {
        tab.outputs.push({
          type: 'warning',
          content: 'Please start your command with "kubectl"',
          timestamp: new Date().toISOString()
        });
      });
      return;
    }

    // 检查 WebSocket 连接
    if (!this.wsConnected) {
      runInAction(() => {
        tab.outputs.push({
          type: 'warning',
          content: 'WebSocket disconnected. Please wait for reconnection...',
          timestamp: new Date().toISOString()
        });
      });
      return;
    }

    // 从命令中提取命名空间
    const commandNamespace = extractNamespaceFromCommand(command);
    let effectiveNamespace = tab.namespace;
    let commandToSend = command;

    // 如果命令中指定了命名空间
    if (commandNamespace) {
      effectiveNamespace = commandNamespace;
      // 从命令中移除 -n/--namespace 参数，因为我们会通过参数传递
      commandToSend = removeNamespaceFromCommand(command);

      // 更新标签页的命名空间
      runInAction(() => {
        tab.namespace = commandNamespace;
        const cluster = this.clusters.find(c => c.id === tab.clusterId);
        tab.title = `${cluster?.name || tab.clusterId} - ${commandNamespace}`;
      });
    }

    // 验证命令安全性
    const validation = validateCommand(command);
    if (!validation.isSafe) {
      runInAction(() => {
        tab.outputs.push({
          type: 'warning',
          content: validation.warning,
          timestamp: new Date().toISOString()
        });
      });
      return;
    }

    // 添加命令到输出（显示原始命令，并快照当前 prompt）
    const commandPrompt = this.getPrompt(tabId);
    runInAction(() => {
      tab.outputs.push({
        type: 'command',
        content: command,
        prompt: commandPrompt,
        timestamp: new Date().toISOString()
      });

      // 添加到历史记录（使用原始命令）
      if (command.trim()) {
        tab.commandHistory.push(command);
        tab.historyIndex = tab.commandHistory.length;
      }

      // 标记为正在执行
      tab.executing = true;
      tab.status = 'executing';
    });

    // 通过 WebSocket 发送命令（使用清理后的命令和提取的命名空间）
    const sessionId = kubectlWebSocketService.executeCommand(
      tab.clusterId,
      effectiveNamespace,
      commandToSend,
      {
        onStatus: (data) => {
          runInAction(() => {
            const currentTab = this.tabs.find(t => t.id === tabId);
            if (!currentTab) return;

            if (data.status === 'executing') {
              currentTab.executing = true;
              currentTab.status = 'executing';
            } else if (data.status === 'completed') {
              currentTab.executing = false;
              currentTab.status = 'connected';
              // 清理会话处理器
              kubectlWebSocketService.removeHandlers(sessionId);
            } else if (data.status === 'cancelled') {
              currentTab.executing = false;
              currentTab.status = 'connected';
              currentTab.outputs.push({
                type: 'warning',
                content: '^C',
                timestamp: new Date().toISOString()
              });
              kubectlWebSocketService.removeHandlers(sessionId);
            } else if (data.status === 'failed') {
              currentTab.executing = false;
              currentTab.status = 'connected';
              kubectlWebSocketService.removeHandlers(sessionId);
            }
          });
        },
        onOutput: (data) => {
          runInAction(() => {
            const currentTab = this.tabs.find(t => t.id === tabId);
            if (!currentTab) return;

            currentTab.outputs.push({
              type: 'output',
              content: data.content || '',
              timestamp: new Date().toISOString()
            });
          });
        },
        onError: (data) => {
          runInAction(() => {
            const currentTab = this.tabs.find(t => t.id === tabId);
            if (!currentTab) return;

            currentTab.outputs.push({
              type: 'error',
              content: data.message || 'Unknown error',
              timestamp: new Date().toISOString()
            });
          });
        },
      }
    );

    runInAction(() => {
      const currentTab = this.tabs.find(t => t.id === tabId);
      if (currentTab) {
        currentTab.currentSessionId = sessionId;
      }
    });
  }

  // 获取自动完成建议
  getAutocompleteSuggestions(tabId, input, cursor) {
    const tab = this.tabs.find(t => t.id === tabId);
    if (!tab) return [];

    // 如果 WebSocket 已连接，使用服务端自动完成
    if (this.wsConnected) {
      // 返回空数组，实际建议通过异步回调返回
      // 这里需要改为异步模式
      return this.fetchAutocompleteSuggestions(tabId, input, cursor);
    }

    // 否则使用本地解析获取建议
    const context = {
      clusters: this.clusters,
      namespaces: this.namespaces[tab.clusterId] || [],
      availableClusters: this.clusters.map(c => c.name)
    };
    return getAutocompleteSuggestions(input, cursor, context);
  }

  // 从服务器获取自动完成建议（异步）
  fetchAutocompleteSuggestions(tabId, input, cursor) {
    const tab = this.tabs.find(t => t.id === tabId);
    if (!tab) return Promise.resolve([]);

    // 存储当前请求的建议，用于在组件中访问
    tab.pendingSuggestions = [];

    return new Promise((resolve) => {
      kubectlWebSocketService.getAutocomplete(
        input,
        cursor,
        tab.clusterId,
        tab.namespace,
        (data) => {
          runInAction(() => {
            const currentTab = this.tabs.find(t => t.id === tabId);
            if (!currentTab) return;

            currentTab.pendingSuggestions = data.suggestions || [];
            resolve(data.suggestions || []);
          });
        }
      );
    });
  }

  // 获取待处理的自动完成建议
  getPendingSuggestions(tabId) {
    const tab = this.tabs.find(t => t.id === tabId);
    return tab?.pendingSuggestions || [];
  }

  // 浏览历史命令
  navigateHistory(tabId, direction) {
    const tab = this.tabs.find(t => t.id === tabId);
    if (!tab || tab.commandHistory.length === 0) return;

    runInAction(() => {
      if (direction === 'up') {
        // 向上浏览更旧的命令
        if (tab.historyIndex > 0) {
          tab.historyIndex--;
          tab.currentInput = tab.commandHistory[tab.historyIndex];
        }
      } else if (direction === 'down') {
        // 向下浏览更新的命令
        if (tab.historyIndex < tab.commandHistory.length - 1) {
          tab.historyIndex++;
          tab.currentInput = tab.commandHistory[tab.historyIndex];
        } else {
          tab.historyIndex = tab.commandHistory.length;
          tab.currentInput = '';
        }
      }
    });
  }

  // 清空输出
  clearOutputs(tabId) {
    runInAction(() => {
      const tab = this.tabs.find(t => t.id === tabId);
      if (tab) {
        tab.outputs = [];
      }
    });
  }

  // 添加输出
  addOutput(tabId, output) {
    runInAction(() => {
      const tab = this.tabs.find(t => t.id === tabId);
      if (tab) {
        tab.outputs.push({
          ...output,
          timestamp: output.timestamp || new Date().toISOString()
        });
      }
    });
  }

  // 更新当前输入
  updateCurrentInput(tabId, value) {
    const tab = this.tabs.find(t => t.id === tabId);
    if (tab) {
      runInAction(() => {
        tab.currentInput = value;
      });
    }
  }

  // 获取当前激活的标签
  get activeTab() {
    return this.tabs.find(tab => tab.id === this.activeTabId) || null;
  }

  // 生成提示符
  getPrompt(tabId) {
    const tab = this.tabs.find(t => t.id === tabId);
    if (!tab) return 'kubectl >';

    const cluster = this.clusters.find(c => c.id === tab.clusterId);
    return generatePrompt(cluster?.name || tab.clusterId, tab.namespace);
  }

  // 中断当前命令
  abortCommand(tabId) {
    const tab = this.tabs.find(t => t.id === tabId);
    if (!tab || !tab.executing) return;

    // 通过 WebSocket 发送取消命令
    if (tab.currentSessionId && this.wsConnected) {
      kubectlWebSocketService.cancelCommand(tab.currentSessionId);
    }

    // 本地更新状态
    runInAction(() => {
      tab.executing = false;
      tab.status = 'connected';
      if (tab.currentSessionId) {
        kubectlWebSocketService.removeHandlers(tab.currentSessionId);
        tab.currentSessionId = null;
      }
    });
  }

  // 清理资源
  dispose() {
    // 断开 WebSocket 连接
    kubectlWebSocketService.disconnect();

    runInAction(() => {
      this.tabs = [];
      this.activeTabId = null;
    });
  }

  // 切换主题
  toggleTheme() {
    this.theme = this.theme === 'dark' ? 'light' : 'dark';
    localStorage.setItem('kubectl-console-theme', this.theme);
  }

  // 设置主题
  setTheme(theme) {
    this.theme = theme;
    localStorage.setItem('kubectl-console-theme', theme);
  }

  // 设置字号
  setFontSize(size) {
    this.fontSize = size;
    localStorage.setItem('kubectl-console-fontsize', size);
  }

  // 获取字号缩放比例
  get fontScale() {
    const scales = {
      '1x': 1,
      '1.5x': 1.5,
      '2x': 2,
      '3x': 3
    };
    return scales[this.fontSize] || 1;
  }
}

export default new KubectlConsole();
